import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { studioStarters, studioProjects } from '@/lib/api';

interface Starter {
  id: string;
  name: string;
  category: string;
  description: string;
  required_connections: string[];
  tags: string[];
}

const CATEGORIES = ['all', 'client_portal', 'security', 'helpdesk', 'monitoring', 'compliance', 'reporting', 'onboarding', 'knowledge', 'vendor', 'communication'];

export function StarterLibrary() {
  const navigate = useNavigate();
  const [starters, setStarters] = useState<Starter[]>([]);
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [buildingId, setBuildingId] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await studioStarters.list(category !== 'all' ? { category } : {});
        setStarters((data.starters ?? []) as Starter[]);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [category]);

  async function buildFromStarter(starterId: string) {
    setBuildingId(starterId);
    try {
      const project = await studioProjects.create({ name: 'New App', starter_id: starterId });
      navigate(`/builder/${project.id}`);
    } finally {
      setBuildingId(null);
    }
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Starters</h1>
        <p className="text-muted-foreground mt-1">Pre-built app templates for common MSP use cases</p>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
              category === cat
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {cat.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-40 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      ) : starters.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          No starters in this category yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {starters.map((starter) => (
            <div key={starter.id} className="border border-border rounded-xl p-5 bg-card hover:border-primary/50 transition-colors">
              <h3 className="font-medium text-foreground mb-1">{starter.name}</h3>
              <p className="text-xs text-muted-foreground mb-3">{starter.description}</p>
              {starter.tags.length > 0 && (
                <div className="flex gap-1 flex-wrap mb-3">
                  {starter.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <button
                onClick={() => void buildFromStarter(starter.id)}
                disabled={buildingId === starter.id}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 py-2 rounded-lg text-sm font-medium"
              >
                {buildingId === starter.id ? 'Building…' : 'Use This Starter'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
